import { onAppBackgrounded } from "@/native/appLifecycle";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { createDraftStorage, DraftStorage } from "./voiceDraftStorage";
import {
  defaultVoiceSessionDeps,
  VoiceSessionController,
  VoiceSessionSnapshot,
} from "./voiceSessionController";

/**
 * React binding for VoiceSessionController.
 *
 * The controller outlives renders (ref) and is disposed on unmount —
 * disposal keeps any in-flight draft persisted, so navigating away
 * mid-recording surfaces the recovery flow on return rather than losing
 * text. Backgrounding flushes the draft unthrottled (appStateChange on
 * native, visibilitychange on web).
 */
export function useVoiceSession(): {
  controller: VoiceSessionController;
  snapshot: VoiceSessionSnapshot;
  draftStorage: DraftStorage;
} {
  const storageRef = useRef<DraftStorage | null>(null);
  if (!storageRef.current) {
    storageRef.current = createDraftStorage();
  }
  const controllerRef = useRef<VoiceSessionController | null>(null);
  if (!controllerRef.current || controllerRef.current.isDisposed()) {
    // Strict Mode remounts dispose the first controller; a disposed one is
    // inert, so mint a fresh instance (nothing user-visible has started yet).
    controllerRef.current = new VoiceSessionController(
      defaultVoiceSessionDeps(storageRef.current),
    );
  }
  const controller = controllerRef.current;

  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
  );

  useEffect(() => {
    return onAppBackgrounded(() => void controller.flushPersist());
  }, [controller]);

  useEffect(() => {
    return () => controller.dispose();
  }, [controller]);

  return { controller, snapshot, draftStorage: storageRef.current };
}
